import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { styled } from '@mui/material/styles';
import { TypographyStyled } from 'src/common/components/ConfigurableTable/Components/Cells/TextCell';
import { TableTooltip } from 'src/common/components/Tooltip/TableTooltip';
import {
  TARIFF_SUMMARY_CONSUMPTION_USAGE_LEVEL_COLOR_MAP,
  TARIFF_SUMMARY_CONSUMPTION_FORECAST_LEVEL_COLOR_MAP,
  TARIFF_SUMMARY_CONSUMPTION_LEVEL,
} from '../constants/tariffSummaryConstant';
import { TariffSummaryConsumptionLevel } from '../models/tariffSummaryModels';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

type TariffSummaryUsageCellDisplayProps = (
  | { usage: null; forecast: null; allowence: null; consumptionLevelUsage: null; consumptionLevelForecast: null }
  | { usage: number; forecast: number; allowence: number; consumptionLevelUsage: TariffSummaryConsumptionLevel; consumptionLevelForecast: TariffSummaryConsumptionLevel }
) & { valueFormater?: (value: number) => string };

const TariffSummarySMSUsageCellRoot = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `1fr ${theme.spacing(80)}`,
  columnGap: theme.spacing(8),
}));

const UsageBars = styled(LinearProgress)<{
  consumptionLevel: TariffSummaryConsumptionLevel;
  consumptionColorMap: Record<TariffSummaryConsumptionLevel, string>;
  tableVariant?: 'primary' | 'secondary';
}>(
  ({
    consumptionColorMap,
    consumptionLevel,
    tableVariant = 'primary',
    theme: { palette, spacing },
  }) => {
    const noAllowance = consumptionLevel === TARIFF_SUMMARY_CONSUMPTION_LEVEL.NO_ALLOWANCE;
    return {
      height: spacing(10),
      borderRadius: spacing(5),
      opacity: noAllowance ? '0.3' : 1,
      background: noAllowance ? palette.grey[300] : 'transparent',
      border: noAllowance ? 'none' : `1px solid ${palette.grey[400]}`,
      [`& .${linearProgressClasses.bar}`]: noAllowance
        ? {
            display: 'none',
          }
        : {
            opacity: tableVariant === 'secondary' ? 0.6 : 1,
            backgroundColor: consumptionColorMap[consumptionLevel],
            borderRadius: spacing(5),
          },
    };
  },
);

const BarWrapper = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(3),
}));

export const TariffSummaryUsageCellDisplay = ({
  valueFormater,
  usage,
  forecast,
  allowence,
  consumptionLevelForecast,
  consumptionLevelUsage,
}: TariffSummaryUsageCellDisplayProps) => {
  const { t } = useTranslation();

  const description = useMemo(
    () => t('tariffSummary.table.progessBarDescription').split('\n'),
    [t],
  );

  if (usage == null) {
    const noDataText = t('tariffSummary.table.noData');
    return (
      <TariffSummarySMSUsageCellRoot>
        <TypographyStyled
          variant="tablePrimary"
          color="text.secondary"
          component="div"
          data-testid="primaryText"
          sx={{ textAlign: 'right' }}
        >
          {noDataText}
        </TypographyStyled>
        <div />
        <TypographyStyled
          variant="tableSecondary"
          color="text.secondary"
          component="div"
          data-testid="secondaryText"
          sx={{ textAlign: 'right' }}
        >
          {noDataText}
        </TypographyStyled>
        <div />
      </TariffSummarySMSUsageCellRoot>
    );
  }

  const usagePercent = (usage / allowence) * 100;
  const forecastPercent = (forecast / allowence) * 100;

  const usageToDisplay = valueFormater ? valueFormater(usage) : usage;
  const forecastToDisplay = valueFormater ? valueFormater(forecast) : forecast;

  return (
    <TariffSummarySMSUsageCellRoot>
      <TypographyStyled
        variant="tablePrimary"
        color="text.primary"
        component="div"
        data-testid="primaryText"
        sx={{ textAlign: 'right' }}
      >
        <TableTooltip value={usageToDisplay?.toString()}>
          <span>{usageToDisplay}</span>
        </TableTooltip>
      </TypographyStyled>
      <BarWrapper>
        <TableTooltip
          value={`${Math.round(usagePercent)} %`}
          disabled={consumptionLevelUsage === TARIFF_SUMMARY_CONSUMPTION_LEVEL.NO_ALLOWANCE}
          delay={500}
        >
          <UsageBars
            tableVariant="primary"
            variant="determinate"
            value={Math.min(usagePercent, 100)}
            aria-label="usage"
            aria-valuenow={usage}
            aria-valuemax={allowence}
            consumptionLevel={consumptionLevelUsage}
            data-consumptionLevel={consumptionLevelUsage}
            data-percentValue={
              consumptionLevelUsage === TARIFF_SUMMARY_CONSUMPTION_LEVEL.NO_ALLOWANCE
                ? 0
                : Math.round(usagePercent)
            }
            consumptionColorMap={TARIFF_SUMMARY_CONSUMPTION_USAGE_LEVEL_COLOR_MAP}
          />
        </TableTooltip>
      </BarWrapper>
      <TypographyStyled
        variant="tableSecondary"
        color="text.secondary"
        component="div"
        data-testid="secondaryText"
        sx={{ textAlign: 'right' }}
      >
        <TableTooltip value={forecastToDisplay?.toString()} description={description}>
          <span>{forecastToDisplay}</span>
        </TableTooltip>
      </TypographyStyled>

      <BarWrapper>
        <TableTooltip
          value={`${Math.round(forecastPercent)} %`}
          description={description}
          disabled={consumptionLevelForecast === TARIFF_SUMMARY_CONSUMPTION_LEVEL.NO_ALLOWANCE}
          delay={500}
        >
          <UsageBars
            variant="determinate"
            tableVariant="secondary"
            data-consumptionLevel={consumptionLevelForecast}
            value={Math.min(forecastPercent, 100)}
            aria-label="forecast"
            aria-valuenow={forecast}
            aria-valuemax={allowence}
            data-percentValue={
              consumptionLevelUsage === TARIFF_SUMMARY_CONSUMPTION_LEVEL.NO_ALLOWANCE
                ? 0
                : Math.round(forecastPercent)
            }
            consumptionLevel={consumptionLevelForecast}
            consumptionColorMap={TARIFF_SUMMARY_CONSUMPTION_FORECAST_LEVEL_COLOR_MAP}
          />
        </TableTooltip>
      </BarWrapper>
    </TariffSummarySMSUsageCellRoot>
  );
};
